import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createAthleteFilter, type Api } from "api-client";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  Input,
  useToast,
} from "ui";
import { z } from "zod";

const createAthleteFilterFormSchema = z.object({
  name: z.string().trim().min(1).max(60),
});

type CreateAthleteFilterFormValues = z.infer<
  typeof createAthleteFilterFormSchema
>;

interface CreateAthleteFilterDialogProps {
  open: boolean;
  criteria: NonNullable<Api.CreateAthleteFilter.Input>["criteria"];
  onOpenChange: (open: boolean) => void;
  onCreated: (filterId: string) => void;
}

export function CreateAthleteFilterDialog(
  props: CreateAthleteFilterDialogProps,
) {
  const { t } = useTranslation();
  const toast = useToast();
  const form = useForm<CreateAthleteFilterFormValues>({
    resolver: zodResolver(createAthleteFilterFormSchema),
    mode: "onChange",
    defaultValues: { name: "" },
  });

  const createMutation = useMutation({
    mutationFn: async (values: CreateAthleteFilterFormValues) => {
      const response = await createAthleteFilter({
        body: {
          name: values.name,
          criteria: props.criteria,
        },
      });
      return response.data.data;
    },
    onSuccess: (filter) => {
      form.reset();
      props.onOpenChange(false);
      props.onCreated(filter.id);
    },
    onError: (error) => {
      toast.toast({
        title: t("athletesPage.filters.createError"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  function handleOpenChange(open: boolean) {
    props.onOpenChange(open);

    if (!open) {
      form.reset();
    }
  }

  function handleSubmit(values: CreateAthleteFilterFormValues) {
    createMutation.mutate(values);
  }

  return (
    <Dialog open={props.open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <DialogHeader>
              <DialogTitle>{t("athletesPage.filters.createTitle")}</DialogTitle>
              <DialogDescription>
                {t("athletesPage.filters.createDescription")}
              </DialogDescription>
            </DialogHeader>
            <div className="py-5">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormLabel>{t("athletesPage.filters.name")}</FormLabel>
                      <FormControl>
                        <Input {...field} autoFocus />
                      </FormControl>
                    </FormItem>
                  );
                }}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  handleOpenChange(false);
                }}
              >
                {t("athletesPage.filters.cancel")}
              </Button>
              <Button
                type="submit"
                loading={createMutation.isPending}
                disabled={!form.formState.isValid}
              >
                {t("athletesPage.filters.save")}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
